import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { SystemRole } from '@fleet/shared';
import { api, setImpersonatedUserId } from '../api/client';
import { usersApi } from '../api/users';
import { useAuth } from './useAuth';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ErrorNotice } from '../ui/ErrorNotice';
import { inputClass } from '../ui/Field';

/**
 * Die Anwendung mit den Rechten einer anderen Person ansehen.
 *
 * Der Anlass: Eine Administration sieht nie, was die Belegschaft sieht.
 * Menüpunkte, Pflichtfelder und Hinweise hängen an Rollen und Fahrzeuggruppen —
 * wer alle Rechte hat, bekommt eine Oberfläche, die es für niemanden sonst gibt,
 * und bemerkt einen Fehler darin nicht. Genau das ist hier passiert: Der
 * Datenschutz-Kasten im Buchungsformular war für die Administration unsichtbar,
 * und die Prüfung „sieht gut aus" bezog sich auf eine Ansicht, die keine
 * Mitarbeiterin je zu Gesicht bekommt.
 *
 * Was diese Ansicht **nicht** ist: eine Möglichkeit, im Namen anderer zu
 * handeln. Der Server weist jeden Schreibzugriff ab. Sonst entstünden Buchungen
 * und Freigaben, unter denen der Name einer Person steht, die nichts davon
 * weiß — im Protokoll stünde eine schlicht falsche Angabe.
 */
/**
 * Umschalten der Ansicht.
 *
 * Beim Wechsel wird der gesamte Zwischenspeicher verworfen. Ohne das blieben
 * Antworten stehen, die für die vorige Identität geholt wurden — Buchungen mit
 * Zweckangabe etwa, die die angesehene Person gar nicht sehen darf. Die Ansicht
 * wäre dann eine Mischung aus beiden und beantwortete die Frage nicht mehr, für
 * die es sie gibt.
 */
function useSwitchIdentity() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return (userId: string | null) => {
    setImpersonatedUserId(userId);
    queryClient.clear();
    // Zurück auf den Kalender: Die Verwaltungsseiten sind für die meisten
    // Menschen gesperrt, und eine Fehlermeldung wäre ein merkwürdiger Empfang.
    void navigate('/');
  };
}

/**
 * Das Band über der Kopfzeile, solange eine fremde Ansicht läuft.
 *
 * Unübersehbar und klebend, und das ist keine Geschmacksfrage: Wer vergisst,
 * dass er fremd schaut, hält eine leere Verwaltungsnavigation für einen Fehler
 * und sucht ihn an der falschen Stelle.
 */
export function ImpersonationBar() {
  const auth = useAuth();
  const switchIdentity = useSwitchIdentity();

  const active = auth.me?.impersonatedBy;
  if (!active) return null;

  return (
    <div className="sticky top-0 z-40 border-b-2 border-amber-500 bg-amber-100">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-2.5">
        <p className="text-sm text-amber-950">
          Ansicht als <strong className="font-semibold">{auth.me?.displayName}</strong> — nur
          lesend, es wird nichts gespeichert.
          <span className="ml-2 text-amber-800">
            Angemeldet bist du als {active.displayName}.
          </span>
        </p>
        <Button size="sm" variant="primary" onClick={() => switchIdentity(null)}>
          Ansicht beenden
        </Button>
      </div>
    </div>
  );
}

/** Einstieg in der Kopfzeile. Nur für die Administration. */
export function ImpersonationButton() {
  const auth = useAuth();
  const switchIdentity = useSwitchIdentity();
  const [picking, setPicking] = useState(false);

  const start = useMutation({
    mutationFn: (userId: string) => api.startImpersonation(userId),
    onSuccess: (target) => {
      setPicking(false);
      switchIdentity(target.userId);
    },
  });

  /**
   * Während einer laufenden Ansicht verschwindet der Knopf — und zwar von
   * selbst, weil `roles` dann die der angesehenen Person sind. Zum Wechseln
   * beendet man erst; das ist ein Klick mehr und ein Zustand weniger, in dem
   * unklar ist, wessen Rechte gerade gelten.
   *
   * Der Einstieg bleibt der Administration vorbehalten, nicht der
   * Fuhrparkleitung: Die verwaltet Stammdaten, sie beaufsichtigt nicht die
   * Anwendung.
   */
  if (!auth.hasRole(SystemRole.ADMIN)) return null;

  return (
    <>
      <Button size="sm" onClick={() => setPicking(true)}>
        Ansicht als …
      </Button>

      {picking && (
        <PersonPicker
          onClose={() => setPicking(false)}
          onPick={(userId) => start.mutate(userId)}
          isPending={start.isPending}
          error={start.error}
        />
      )}
    </>
  );
}

function PersonPicker({
  onClose,
  onPick,
  isPending,
  error,
}: {
  onClose: () => void;
  onPick: (userId: string) => void;
  isPending: boolean;
  error: unknown;
}) {
  const [term, setTerm] = useState('');

  const people = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 5 * 60_000,
  });

  const needle = term.trim().toLowerCase();
  const matches = (people.data ?? []).filter(
    (person) =>
      !needle ||
      person.displayName.toLowerCase().includes(needle) ||
      (person.email ?? '').toLowerCase().includes(needle),
  );

  return (
    <Modal
      title="Ansicht als andere Person"
      description="Du siehst die Anwendung mit deren Rechten. Geschrieben wird dabei nichts — der Vorgang wird protokolliert."
      onClose={onClose}
    >
      <div className="space-y-3">
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Name oder E-Mail"
          className={inputClass}
          autoFocus
        />

        {people.isPending && <p className="text-sm text-slate-500">Wird geladen …</p>}
        {people.isError && <ErrorNotice error={people.error} />}
        {/* Ternär statt `&&`: Bei `unknown` wäre der falsche Zweig kein ReactNode. */}
        {error ? <ErrorNotice error={error} /> : null}

        {people.data && matches.length === 0 && (
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            Keine Treffer. Auswählbar sind nur Personen, die sich schon einmal angemeldet
            haben — vorher gibt es keinen Datensatz, dessen Rechte sich ansehen ließen.
          </p>
        )}

        <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
          {matches.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                disabled={isPending}
                onClick={() => onPick(person.id)}
                className="flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50 disabled:opacity-50"
              >
                <span>
                  <span className="block text-sm font-medium">{person.displayName}</span>
                  {person.email && (
                    <span className="block text-xs text-slate-500">{person.email}</span>
                  )}
                </span>
                <span aria-hidden className="text-slate-400">
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="flex justify-end border-t border-slate-200 pt-4">
          <Button onClick={onClose}>Abbrechen</Button>
        </div>
      </div>
    </Modal>
  );
}
